import { FC } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, Checkbox } from '@features/ui';
import {
  CompletePayoutRequestFormData,
  schemaCompletePayoutRequest,
  TPayout,
} from '@src/types';
import { capitalize, formatCurrency, formatDateByMST } from '@lib/utils.ts';
import { CloseIcon } from '@features/ui/icons';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { makePayment } from '@src/services/payout.ts';
import { toast } from 'react-toastify';

interface CompletePayoutRequestProps {
  item?: TPayout;
  refreshData?: () => Promise<void>;
  onOpenChange: (open: boolean) => void;
}
export const CompletePayoutRequest: FC<CompletePayoutRequestProps> = ({
  item,
  refreshData,
  onOpenChange,
}) => {
  const { control, handleSubmit, reset, formState } =
    useForm<CompletePayoutRequestFormData>({
      resolver: yupResolver(schemaCompletePayoutRequest),
    });

  const payoutMethod = item?.payout_method ?? '';
  const payoutAmount = Number(item?.amount ?? 0);
  const rithmicDeduction = Number(item?.rithmic_deduction ?? 0);
  const isPaPayout = Boolean(item?.is_pa);
  const showRithmicDeductionLine =
    isPaPayout && rithmicDeduction > 0 && rithmicDeduction !== payoutAmount;
  const requiresRithmicConfirmation =
    isPaPayout && rithmicDeduction > 0 && !item?.rithmic_deduction_added_on;
  const showRithmicConfirmation = isPaPayout && rithmicDeduction > 0;
  const showAchOption = payoutMethod === 'wire';
  const isRiseWorksPayment = payoutMethod === 'riseworks';

  const handleOpenChange = (open: boolean) => {
    reset({
      note: undefined,
      confirm_rithmic: undefined,
      ach_flag: undefined,
    });
    onOpenChange(open);
  };

  const handleSubmitForm = async (data: CompletePayoutRequestFormData) => {
    if (!item) return;

    if (requiresRithmicConfirmation && !data.confirm_rithmic) {
      toast.error('You must confirm the Rithmic deduction you have made!');
      return;
    }

    const payload: CompletePayoutRequestFormData = {
      note: data.note,
      confirm_rithmic: showRithmicConfirmation ? data.confirm_rithmic : undefined,
      ach_flag: showAchOption ? Boolean(data.ach_flag) : undefined,
    };

    try {
      await makePayment(item.id, payload);
      await refreshData?.();
      handleOpenChange(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message);
    }
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleSubmitForm)}>
      <div>
        <Dialog.Title className="m-0 text-2xl font-medium">
          Complete {isPaPayout ? '' : 'Competition '}Payout Request
        </Dialog.Title>
        <div className={'flex flex-col gap-y-8 mb-5 mt-6.5'}>
          <Dialog.Description className="text-xs leading-normal">
            Please check the following information for the payout on account{' '}
            {item?.leeloo_account.rithmic_account_name}
          </Dialog.Description>
          <div className={'flex flex-col gap-3'}>
            <p className={'text-xl font-medium'}>
              1. Payout Amount: ${formatCurrency(payoutAmount, 2)}
            </p>
            {showRithmicDeductionLine ? (
              <p className={'text-xs'}>
                Rithmic Deduction ${formatCurrency(rithmicDeduction, 2)}
              </p>
            ) : null}
          </div>
          <div className={'flex flex-col gap-3'}>
            <p className={'text-xl font-medium'}>
              2. Payout Method: {capitalize(payoutMethod)}
            </p>
            {payoutMethod === 'paypal' && item?.paypal_address ? (
              <p className={'text-xs'}>PayPal: {item.paypal_address}</p>
            ) : null}
            {payoutMethod === 'wire' ? (
              <p className={'text-xs'}>
                Wire transfer details are provided on this payout request.
              </p>
            ) : null}
            {payoutMethod === 'coupon' ? (
              <p className={'text-xs'}>This payout will be completed by coupon.</p>
            ) : null}
            {payoutMethod === 'riseworks' ? (
              <p className={'text-xs'}>
                This payout will call RiseWorks payment processing.
              </p>
            ) : null}
            <p className={'text-lime'}>
              The above payout information was validated by{' '}
              {item?.approved_by_user?.first_name ?? ''}{' '}
              {item?.approved_by_user?.last_name ?? ''} on{' '}
              {formatDateByMST(item?.approved_on ?? '')}
            </p>
          </div>
          {item?.payment_method_note ? (
            <div className={'flex flex-col gap-3'}>
              <p className={'text-xl font-medium'}>3. Note</p>
              <p>{item?.payment_method_note}</p>
            </div>
          ) : null}
          <div className={'flex flex-col gap-3'}>
            <p className={'text-xl font-medium'}>Make Payment</p>
            <p className={'text-xs'}>
              You can add a payment reference or note below.
            </p>
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <textarea
                  {...field}
                  className="h-[124px] border-1 border-[#C6C6C6] px-2.5 rounded-[3px] py-2 text-sm"
                />
              )}
            />
            {showRithmicConfirmation ? (
              <Controller
                control={control}
                name="confirm_rithmic"
                render={({ field }) => (
                  <Checkbox
                    checked={Boolean(field.value)}
                    disabled={!requiresRithmicConfirmation}
                    id="confirm_rithmic"
                    label={`Confirm Rithmic Deduction $${formatCurrency(rithmicDeduction, 2)}`}
                    onCheckedChange={(val) => field.onChange(val === true)}
                  />
                )}
              />
            ) : null}
            {showAchOption ? (
              <Controller
                control={control}
                name="ach_flag"
                render={({ field }) => (
                  <Checkbox
                    checked={Boolean(field.value)}
                    id="ach_flag"
                    label={'Pay by ACH?'}
                    onCheckedChange={(val) => field.onChange(val === true)}
                  />
                )}
              />
            ) : null}
          </div>
          <div className={'flex gap-x-7'}>
            <Button
              size={'sm'}
              variant={'lime'}
              loading={formState.isSubmitting}
            >
              {isRiseWorksPayment ? 'MAKE RISEWORKS PAYMENT' : 'MARK AS PAID'}
            </Button>
            <Button
              size={'sm'}
              type={'button'}
              onClick={() => handleOpenChange(false)}
            >
              Close
            </Button>
          </div>
        </div>
        <Dialog.Close asChild>
          <Button
            aria-label="Close"
            className="[&_svg]:!size-auto absolute right-[35px] top-[43px] inline-flex size-[25px] appearance-none items-center justify-center rounded-full"
            variant={'ghost'}
            onClick={() => handleOpenChange(false)}
          >
            <CloseIcon />
          </Button>
        </Dialog.Close>
      </div>
    </form>
  );
};
